import React, { useState, useEffect, useCallback } from "react";
import { Table, Pagination, Row, Col, Tooltip, Popover, Collapse } from "antd";
import axios from "axios";

import { formatDateTime } from "../../helpers/date.js";
import ServiceProviderTag from "../shared/ServiceProviderTag.js";
import TransactionButtons from "./TransactionButtons.js";
import { InfoCircleOutlined, CaretRightOutlined } from "@ant-design/icons";
import TransactionFilters from "./TransactionFilters.js";

const TransactionList = ({ types }) => {
  const [transactions, setTransactions] = useState();
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 50 });
  const [isLoading, setIsLoading] = useState(false);

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    const res = await axios.get(`api/transactions`, {
      params: {
        types: types,
        limit: pagination.limit,
        page: pagination.currentPage,
        ...(filters.search && {
          searchField: filters.searchField,
          search: filters.search,
        }),
        ...(filters.startDate !== null && {
          startDate: filters.startDate?.startOf("day")?.toISOString(),
        }),
        ...(filters.endDate !== null && {
          endDate: filters.endDate?.endOf("day")?.toISOString(),
        }),
        ...(filters.tagging && { tagging: filters.tagging }),
      },
    });
    setTransactions(res.data.data);
    setPagination(res.data.pagination);
    setIsLoading(false);
  }, [pagination.currentPage, pagination.limit, filters, types]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const columns = {
    consignment_purchase: [
      {
        title: "Transaction ID",
        dataIndex: "objectId",
        width: 130,
      },
      {
        title: "User Email",
        dataIndex: "merchant",
        key: "username",
        width: 220,
        render: (text) => (
          <Tooltip title={text?.user?.username || "-"}>
            {text?.user?.email || "-"}{" "}
          </Tooltip>
        ),
      },
      {
        title: "Order ID",
        dataIndex: "order",
        key: "orderId",
        width: 130,
        render: (text, row) => text?.objectId || row.ref_no || "-",
      },
      {
        title: "Tracking No",
        dataIndex: "order",
        key: "trackingNo",
        width: 180,
        render: (text) => text?.consign_no || "-",
      },
      {
        title: "Courier",
        dataIndex: "order",
        width: 100,
        render: (text, row) =>
          text?.serviceProvider ? (
            <ServiceProviderTag serviceProviderKey={text.serviceProvider} />
          ) : (
            ""
          ),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        width: 100,
        render: (text) => (text ? text.toFixed(2) : "-"),
      },
      {
        title: (
          <span>
            Original Price{" "}
            <Tooltip title="Original Price recorded during Checkout. This value will never change.">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        ),
        dataIndex: ["order", "snapshot"],
        width: 140,
        render: (snapshot) => snapshot?.price || "-",
      },
      {
        title: (
          <span>
            Prev Price{" "}
            <Tooltip title="Price before the last Weight Dispute update.">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        ),
        dataIndex: ["order", "original"],
        width: 120,
        render: (original) => original?.price || "-",
      },
      {
        title: (
          <span>
            Actual Price{" "}
            <Tooltip title="Latest Price.">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        ),
        dataIndex: ["order", "price"],
        width: 130,
      },
      {
        title: (
          <span>
            Original COD{" "}
            <Tooltip title="Original COD amount recorded during Checkout. This value will never change.">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        ),
        dataIndex: ["order", "snapshot"],
        ellipsis: true,
        width: 140,
        render: (snapshot) => {
          return snapshot?.cod
            ? `${
                snapshot?.codCurrency?.currency || "RM"
              } ${snapshot?.cod.toFixed(2)}`
            : "-";
        },
      },
      {
        title: "COD",
        dataIndex: ["order", "cod"],
        ellipsis: true,
        width: 90,
        render: (cod, order) => {
          if (cod && order.codCurrency) {
            const { currency, fromChargeRate, fromRemitRate } =
              order.codCurrency;
            const rateInfo = (
              <div>
                {fromChargeRate && (
                  <p>
                    <strong>Charge Rate</strong>: {currency} 1.00 to RM{" "}
                    {fromChargeRate}
                  </p>
                )}
                {fromRemitRate && (
                  <p>
                    <strong>Remit Rate</strong>: {currency} 1.00 to RM{" "}
                    {fromRemitRate}
                  </p>
                )}
              </div>
            );

            return (
              <div>
                <span>{`${currency} ${cod.toFixed(2)}`}</span>
                {` `}
                <Popover content={rateInfo} title="Conversion Rates">
                  <InfoCircleOutlined />
                </Popover>
              </div>
            );
          }

          return cod ? `RM ${cod.toFixed(2)}` : "-";
        },
      },
      {
        title: "Insurance",
        dataIndex: ["order", "insurance"],
        width: 100,
        render: (text) => (text ? text : "-"),
      },
      {
        title: "Postage Price",
        dataIndex: ["order", "prices", "courier", "postage"],
        width: 130,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "SMS Charge",
        dataIndex: ["order", "prices", "courier", "pickupSMS"],
        width: 120,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "Email Charge",
        dataIndex: ["order", "prices", "courier", "pickupEmail"],
        width: 120,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "Whatsapp Charge",
        dataIndex: ["order", "prices", "courier", "pickupWhatsapp"],
        width: 150,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "Reschedule Whatsapp Charge",
        dataIndex: ["order", "prices", "courier", "rescheduleWhatsapp"],
        width: 230,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "Hide Cod Amount Charge",
        dataIndex: ["order", "prices", "charges", "hideCodAmount"],
        width: 200,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "Hide Logo Charge",
        dataIndex: ["order", "prices", "charges", "hideLogo"],
        width: 150,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "Hide Address Charge",
        dataIndex: ["order", "prices", "charges", "hideAddress"],
        width: 170,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "Insurance Charge",
        width: 150,
        dataIndex: ["order", "prices", "courier", "insurance"],
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: (
          <span>
            Original COD Charge{" "}
            <Tooltip title="Original COD Charge recorded during Checkout. This value will never change.">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        ),
        dataIndex: ["order", "snapshot"],
        width: 190,
        render: (snapshot) => {
          return snapshot?.prices?.courier?.cod
            ? Number(snapshot?.prices?.courier?.cod).toFixed(2)
            : "-";
        },
      },
      {
        title: "COD Charge",
        dataIndex: ["order", "prices", "courier", "cod"],
        width: 120,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: (
          <span>
            Original NDR Charge{" "}
            <Tooltip title="Original Next-Day Remittance Charge recorded during Checkout. This value will never change.">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        ),
        dataIndex: ["order", "snapshot"],
        width: 190,
        render: (snapshot) => {
          return snapshot?.prices?.charges?.nextDayRemittance
            ? Number(snapshot?.prices?.charges?.nextDayRemittance).toFixed(2)
            : "-";
        },
      },
      {
        title: "NDR Charge",
        dataIndex: ["order", "prices", "charges", "nextDayRemittance"],
        width: 120,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: (
          <span>
            Original Return Handling Fee Charge{" "}
            <Tooltip title=" Original Return Handling Fee Charge recorded during Checkout. This value will never change.">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        ),
        dataIndex: ["order", "snapshot"],
        render: (snapshot) => {
          return snapshot?.prices?.courier?.returnFee
            ? Number(snapshot?.prices?.courier?.returnFee).toFixed(2)
            : "-";
        },
      },
      {
        title: "Return Handling Fee Charge",
        dataIndex: ["order", "prices", "courier", "returnFee"],
        width: 230,
        render: (text) => (text ? Number(text).toFixed(2) : "-"),
      },
      {
        title: "Method",
        dataIndex: "method",
        width: 180,
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        width: 130,
      },
      {
        title: "Charged At",
        dataIndex: "creditSuccessAt",
        width: 150,
        render: (text) => (text ? formatDateTime(text) : "Failed"),
      },
      {
        title: "Order Created At",
        dataIndex: "order",
        key: "createdAt",
        width: 150,
        render: (text) =>
          text?.createdAt ? formatDateTime(text.createdAt) : "-",
      },
      {
        title: "User Tagging",
        dataIndex: "merchant",
        key: "tagging",
        width: 140,
        render: (text) => text?.user?.tagging || "-",
      },
      {
        title: "User Tagging 2",
        dataIndex: "merchant",
        key: "tagging",
        width: 140,
        render: (text) => text?.user?.tagging || "-",
      },
    ],
    default: [
      {
        title: "Transaction ID",
        dataIndex: "objectId",
        width: 130,
      },
      {
        title: "User Email",
        dataIndex: "merchant",
        key: "username",
        width: 220,
        render: (text) => (
          <Tooltip title={text?.user?.username || "-"}>
            {text?.user?.email || "-"}{" "}
          </Tooltip>
        ),
      },
      {
        title: "Order ID",
        dataIndex: "order",
        key: "orderId",
        render: (text, row) => text?.objectId || row.ref_no || "-",
      },
      {
        title: "Tracking No",
        dataIndex: "order",
        key: "trackingNo",
        render: (text) => text?.consign_no || "-",
      },
      {
        title: "Courier",
        dataIndex: "order",
        render: (text, row) =>
          text?.serviceProvider ? (
            <ServiceProviderTag serviceProviderKey={text.serviceProvider} />
          ) : (
            ""
          ),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        render: (text) => (text ? text.toFixed(2) : "-"),
      },
      {
        title: "Method",
        dataIndex: "method",
        width: 200,
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        width: 250,
      },
      {
        title: "Charged At",
        dataIndex: "creditSuccessAt",
        render: (text) => (text ? formatDateTime(text) : "Failed"),
      },
      {
        title: "Order Created At",
        dataIndex: "order",
        key: "createdAt",
        render: (text) =>
          text?.createdAt ? formatDateTime(text.createdAt) : "-",
      },
      {
        title: "User Tagging",
        dataIndex: "merchant",
        key: "tagging",
        render: (text) => text?.user?.tagging || "-",
      },
    ],
  };

  return (
    <React.Fragment>
      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <TransactionFilters
            filters={filters}
            onFiltersChange={(filters) => setFilters(filters)}
          />
        </Collapse.Panel>
      </Collapse>

      <Row className="mt-2 mb-2" justify="space-between">
        <TransactionButtons
          filters={{
            types: types,
            ...(filters.startDate !== null && {
              startDate: filters.startDate?.startOf("day")?.toISOString(),
            }),
            ...(filters.endDate !== null && {
              endDate: filters.endDate?.endOf("day")?.toISOString(),
            }),
            ...(filters.search && {
              searchField: filters.searchField,
              search: filters.search,
            }),
            ...(filters.tagging && { tagging: filters.tagging }),
            pagination: pagination,
          }}
        />
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>

      <Table
        columns={columns[types] || columns["default"]}
        dataSource={transactions}
        pagination={false}
        scroll={{ x: types == 'consignment_purchase' ? 5000 : 2000, y: 600 }}
        loading={isLoading}
      />  

      <Row justify="end">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default TransactionList;
